import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography
} from '@mui/material';

import LoadingButton from '../../../../../components/buttons/Button';
import useSearchableSelect from '../../../../../components/hooks/useSearchableSelect';

import {
  CreateUsitcChapter99MappingBIZcoreTD,
  UpdateUsitcChapter99MappingBIZcoreTD,
  GetUsitcRevisionsDropdownBIZcoreTD
} from '../../../../../redux/actions/business/core/tradeDefencesActions';

import {
  CLEAR_USITC_REVISIONS_DROPDOWN_BIZ_CORE_TD
} from '../../../../../redux/actionTypes';

import global from "../../../../../global.scss";

const MODES = { CREATE: 1, EDIT: 2 };

const REMEDY_TYPE_CHOICES = [
  { value: "SEC_301_CHINA", label: "Section 301 (China)" },
  { value: "SEC_232_STEEL", label: "Section 232 (Steel)" },
  { value: "SEC_232_ALUMINUM", label: "Section 232 (Aluminum)" },
  { value: "IEEPA_SURCHARGE", label: "IEEPA Surcharge" },
  { value: "RETALIATORY", label: "Retaliatory" },
  { value: "EXCLUSION", label: "Exclusion" },
  { value: "OTHER", label: "Other" },
];

const Data = {
  Revision: null,
  Chapter99Hts: "",
  TargetBaseHtsStart: "",
  TargetBaseHtsEnd: "",
  RemedyType: "",
  EffectiveDate: "",
  ExpirationDate: "",
};

const UsitcChapter99MappingsModal = ({
  open,
  setOpen,
  title,
  update,
  handleGet
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(Data);
  const [mode, setMode] = useState(MODES.CREATE);

  const {
    results: revisionResults,
    loading: revisionSearchLoading,
    handleSearch: handleRevisionSearch,
    reset: resetRevisionSearch,
  } = useSearchableSelect(
    GetUsitcRevisionsDropdownBIZcoreTD,
    (state) => {
      const list = state["business.core.tradeDefence"].usitcRevisionsDropdownList;
      if (list?.records && Array.isArray(list.records)) {
        return list.records;
      }
      return [];
    },
    null,
    { limit: 10, formatQuery: (q) => `&search=${q}` }
  );

  const handleClose = () => {
    setOpen(false);
    setFormData(Data);
    setMode(MODES.CREATE);
    dispatch({ type: CLEAR_USITC_REVISIONS_DROPDOWN_BIZ_CORE_TD });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    try {
      if (open) {
        resetRevisionSearch();
        if (update.defaultMode === MODES.CREATE) {
          setMode(MODES.CREATE);
        } else if (update.defaultMode === MODES.EDIT) {
          setMode(MODES.EDIT);
          setFormData({
            Revision: update.ref_revision_id || null,
            Chapter99Hts: update.chapter_99_hts || "",
            TargetBaseHtsStart: update.target_base_hts_start || "",
            TargetBaseHtsEnd: update.target_base_hts_end || "",
            RemedyType: update.remedy_type || "",
            EffectiveDate: update.effective_date || "",
            ExpirationDate: update.expiration_date || "",
          });
        } else {
          toast.error("Invalid modal");
          handleClose();
        }
      }
    } catch (err) {
      console.log(err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = () => {
    const obj = {
      ref_revision_id: formData.Revision,
      chapter_99_hts: formData.Chapter99Hts,
      target_base_hts_start: formData.TargetBaseHtsStart,
      target_base_hts_end: formData.TargetBaseHtsEnd,
      remedy_type: formData.RemedyType,
      effective_date: formData.EffectiveDate,
      expiration_date: formData.ExpirationDate || null,
    };

    if (mode === MODES.CREATE) {
      dispatch(CreateUsitcChapter99MappingBIZcoreTD(obj, setLoading)).then(() => {
        handleGet(setLoading).then(() => {
          handleClose();
        });
      });
    } else if (mode === MODES.EDIT) {
      dispatch(UpdateUsitcChapter99MappingBIZcoreTD(update.id, obj, setLoading)).then(() => {
        handleGet(setLoading).then(() => {
          handleClose();
        });
      });
    } else {
      toast.error("Invalid modal");
      handleClose();
    }
  };

  const isSubmitDisabled = () => {
    return !(
      formData.Revision &&
      formData.Chapter99Hts !== "" &&
      formData.TargetBaseHtsStart !== "" &&
      formData.TargetBaseHtsEnd !== "" &&
      formData.RemedyType !== "" &&
      formData.EffectiveDate !== ""
    );
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
    >
      <Box className="flexCenterCenterRow" width={"100vw"} height={"100vh"}>
        <Box maxWidth={"sm"} className="modal">
          <Box width={"100%"} sx={{ padding: "15px" }}>
            <Typography variant='h6'
              sx={{ color: global["primary"] }} mb={"20px"}>
              {title}
            </Typography>

            {/* USITC Revision */}
            <Box mb={"15px"}>
              <Autocomplete
                fullWidth
                options={Array.isArray(revisionResults) ? revisionResults : []}
                loading={revisionSearchLoading}
                disabled={mode === MODES.EDIT}
                value={
                  formData.Revision
                  ? (Array.isArray(revisionResults) ? revisionResults : []).find(r => r.id === formData.Revision) || formData.Revision
                  : null
                }
                onInputChange={(e, value) => handleRevisionSearch(value)}
                onChange={(e, value) => setFormData({ ...formData, Revision: value?.id || null })}
                getOptionLabel={(option) =>
                  typeof option === 'string'
                  ? option
                  : (option.revision_name || option.id || "NA")
                }
                isOptionEqualToValue={(option, value) => {
                  if (typeof value === 'string') return option.id === value;
                  return option.id === value?.id;
                }}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2">{option.revision_name || option.id || "NA"}</Typography>
                      {option.effective_date && (
                        <Typography variant="caption" sx={{ color: "grey" }}>
                          Effective: {option.effective_date}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="USITC revision"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {revisionSearchLoading ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>

            {/* Chapter 99 HTS */}
            <Box mb={"15px"}>
              <TextField
                label="Chapter 99 HTS"
                fullWidth
                name="Chapter99Hts"
                value={formData.Chapter99Hts}
                onChange={handleChange}
                inputProps={{ maxLength: 20 }}
              />
            </Box>

            {/* Base HTS Start + End */}
            <Box className="flexCenterSBRow" gap={"15px"} mb={"15px"}>
              <TextField
                label="Base HTS start"
                fullWidth
                name="TargetBaseHtsStart"
                value={formData.TargetBaseHtsStart}
                onChange={handleChange}
                inputProps={{ maxLength: 10 }}
              />
              <TextField
                label="Base HTS end"
                fullWidth
                name="TargetBaseHtsEnd"
                value={formData.TargetBaseHtsEnd}
                onChange={handleChange}
                inputProps={{ maxLength: 10 }}
              />
            </Box>

            {/* Remedy Type */}
            <Box mb={"15px"}>
              <FormControl fullWidth>
                <InputLabel>Remedy type</InputLabel>
                <Select
                  name="RemedyType"
                  value={formData.RemedyType}
                  label="Remedy type"
                  onChange={handleChange}
                >
                  {REMEDY_TYPE_CHOICES.map((choice) => (
                    <MenuItem key={choice.value} value={choice.value}>
                      {choice.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Effective Date + Expiration Date */}
            <Box className="flexCenterSBRow" gap={"15px"} mb={"15px"}>
              <TextField
                label="Effective date"
                fullWidth
                name="EffectiveDate"
                type="date"
                value={formData.EffectiveDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Expiration date (optional)"
                fullWidth
                name="ExpirationDate"
                type="date"
                value={formData.ExpirationDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Box>

          <Divider sx={{ backgroundColor: "rgba(44, 45, 47, 1)" }} />
          <Box className="flexCenterFERow" sx={{ gap: "20px", margin: "15px" }}>
            <Button variant='text' onClick={handleClose}>
              Cancel
            </Button>
            <LoadingButton
              variant='contained'
              loading={loading}
              disabled={isSubmitDisabled()}
              onClick={handleSubmit}
            >
              Submit
            </LoadingButton>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default UsitcChapter99MappingsModal;
